import { FC } from 'react';
import { useConfigContext } from '@fe-core';
import { useIntl } from 'react-intl';
import { cn } from '@fe-learner-common';

const VerticalSeparator = () => (
  <span className="mx-2 h-3 w-px bg-neutral-700" />
);

type FooterProps = {
  className?: string;
  isLoading?: boolean;
};

const Skeleton = () => (
  <div className="flex items-center justify-center space-x-2">
    <div className="h-4 w-[300px] animate-pulse rounded bg-neutral-200" />
    <VerticalSeparator />
    <div className="h-4 w-[100px] animate-pulse rounded bg-neutral-200" />
    <VerticalSeparator />
    <div className="h-4 w-[100px] animate-pulse rounded bg-neutral-200" />
    <VerticalSeparator />
    <div className="h-4 w-[170px] animate-pulse rounded bg-neutral-200" />
  </div>
);

export const BxFooter: FC<FooterProps> = ({ className, isLoading }) => {
  const { config } = useConfigContext();
  const intl = useIntl();
  const realmConfig = config?.realmConfig as Record<string, string>;
  const privacyPolicyUrl = realmConfig['cocosite.privacypolicy.url'];
  const tosUrl = realmConfig['cocosite.tos.url'];

  return (
    <footer
      className={cn(
        'flex flex-wrap items-center justify-center whitespace-nowrap border-t-1 border-neutral-200 p-5 text-body-sm',
        className,
      )}
    >
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <p>
            {intl.formatMessage(
              {
                id: 'footer.company',
                defaultMessage: '© {year} Blanchard. All rights reserved',
              },
              { year: new Date().getFullYear() },
            )}
          </p>
          <VerticalSeparator />
          {privacyPolicyUrl && (
            <>
              <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({
                  id: 'footer.privacy.policy',
                  defaultMessage: 'Privacy Policy',
                })}
              </a>
              <VerticalSeparator />
            </>
          )}
          {tosUrl && (
            <>
              <a href={tosUrl} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({
                  id: 'footer.terms.of.service',
                  defaultMessage: 'Terms & Conditions',
                })}
              </a>
              <VerticalSeparator />
            </>
          )}
          <a
            href="https://learnifier.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage({
              id: 'footer.powered.by.learnifier',
              defaultMessage: 'Powered by Learnifier',
            })}
          </a>
        </>
      )}
    </footer>
  );
};
